<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { vMaska } from 'maska/vue';
  import { Calendar } from 'lucide-vue-next';
  import type { FormElement, DateInputProperties } from '../../../model/form.types';
  import { useLabelPosition } from '../../../model/useLabelPosition';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import { DatePicker } from '@/shared/ui';
  import { MASK_PRESETS } from '@/shared/lib/mask';
  import { useEditableField } from '../../../model/useFormBuilderReadonly';

  interface Props {
    element: FormElement & { properties: DateInputProperties };
  }

  const props = defineProps<Props>();

  const dateValue = ref('');
  const isDatePickerOpen = ref(false);
  const selectedDate = ref<Date | null>(null);

  watch(selectedDate, (newDate: Date | null) => {
    const newDateString = newDate ? newDate.toLocaleDateString('pt-BR') : '';
    if (dateValue.value !== newDateString) {
      dateValue.value = newDateString;
    }
  });

  const isValidDateComponents = (day: number, month: number, year: number): boolean => {
    if (year <= 999 || month < 1 || month > 12 || day < 1) {
      return false;
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    return day <= daysInMonth;
  };

  const isValidDate = (date: Date, day: number, month: number, year: number): boolean => {
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  watch(dateValue, (newString: string) => {
    if (!newString) {
      if (selectedDate.value) {
        selectedDate.value = null;
      }
      return;
    }

    if (newString.length !== 10) return;

    const [day, month, year] = newString.split('/').map(Number);

    if (!isValidDateComponents(day, month, year)) {
      if (selectedDate.value) {
        selectedDate.value = null;
      }
      return;
    }

    const date = new Date(year, month - 1, day);

    if (!isValidDate(date, day, month, year)) {
      if (selectedDate.value) {
        selectedDate.value = null;
      }
      return;
    }

    if (date.getTime() !== selectedDate.value?.getTime()) {
      selectedDate.value = date;
    }
  });

  const { isLabelLeft, inputClasses, labelClasses, labelStyles } = useLabelPosition(
    computed(() => props.element.properties)
  );

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'DATE_INPUT'
  );

  const maskConfig = computed(() => ({
    mask: MASK_PRESETS.date,
    eager: false,
  }));

  const isEditable = useEditableField(() => props.element.properties.editable);

  const openDatePicker = () => {
    if (isEditable.value) {
      isDatePickerOpen.value = true;
    }
  };

  const handleDateSelected = (date: Date | null) => {
    selectedDate.value = date;
    if (!date) {
      dateValue.value = '';
    }
    isDatePickerOpen.value = false;
  };
</script>

<template>
  <div
    class="date-input-element"
    :class="{ 'label-left': isLabelLeft }"
    :style="containerStyles"
  >
    <label
      :for="`input-${element.id}`"
      :class="labelClasses"
      :style="labelStyles"
    >
      {{ element.properties.label }}
    </label>
    <div class="date-input-wrapper">
      <button
        type="button"
        class="calendar-icon"
        :disabled="!isEditable"
        @click="openDatePicker"
      >
        <Calendar :size="18" />
      </button>
      <DatePicker
        v-model:selected-date="selectedDate"
        v-model:is-open="isDatePickerOpen"
        :disable-past-dates="false"
        :default-to-today="element.properties.fieldTypes?.includes('present') ?? false"
        @apply="handleDateSelected"
        @cancel="isDatePickerOpen = false"
      />
      <input
        :id="`input-${element.id}`"
        v-model="dateValue"
        v-maska="maskConfig"
        type="text"
        :name="element.properties.name"
        :placeholder="element.properties.placeholder"
        :required="element.properties.required"
        :aria-label="element.properties.label || element.properties.name"
        :readonly="!isEditable"
        :class="inputClasses"
        @click="openDatePicker"
      >
    </div>
  </div>
</template>

<style scoped>
  .date-input-element {
    font-family: var(--font-base);
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .date-input-element.label-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .date-input-element.label-left label {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .date-input-element.label-left .date-input-wrapper {
    flex: 1;
    min-width: 0;
    max-width: 100%;
  }

  .date-input-wrapper {
    position: relative;
    width: 100%;
  }

  .date-input-wrapper input {
    width: 100%;
    padding-right: 2.5rem;
  }

  .calendar-icon {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: rgb(107 114 128);
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
  }

  .calendar-icon:hover:not(:disabled) {
    color: rgb(55 65 81);
  }

  .calendar-icon:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .date-input-wrapper input {
    font-size: 11px;
    line-height: 16px;
  }

  input::placeholder {
    font-size: 10px;
    opacity: 0.6;
  }
</style>
